import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { ANALYTICS, trackEvent } from '../lib/analytics';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import {
  InvoiceRequestRefused, invoiceRequestDocuments, invoiceRequestRefusal, isFailedRequest, isPendingRequest,
  skippedInvoiceRows,
} from '../domain/invoice';
import { useCollectionWindow } from './useCollectionWindow';

// Invoices.
//
// Generation runs server-side, in the generateInvoice Cloud Function.
//
// The client appends a request to invoiceRequests and watches it; the function
// folds the family's full ledger with the same domain/invoice.js code this app
// uses, allocates a sequential number in a transaction, and writes the invoice.
//
// Neither could be done correctly here. A browser folds only the entries in
// its own cache — offline that silently produces a smaller invoice — and
// sequential numbering needs a transaction, which Firestore does not run
// offline. Offline, the request waits in the write queue, which is what the
// "Creating" badge describes.

const InvoicesContext = createContext(null);
const TRACK_LIMIT = 2000;

export function InvoicesProvider({ children }) {
  const { user } = useAuth();

  // The invoices window (#207 U5). This listener drives `loading`; the
  // requests window below has its own `requestsLoading`.
  const invoiceWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.invoices,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  // The invoiceRequests window (#207 U5), watched so an in-flight generation
  // is visible: a request the function has not picked up yet is "Creating".
  const requestWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.invoiceRequests,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const invoices = invoiceWindow.docs;
  const requests = requestWindow.docs;
  const loading = invoiceWindow.loading;
  // The Invoices tab lists both, so its empty state waits for both (#629):
  // `requestsLoading` is the second window's own answer.
  const requestsLoading = requestWindow.loading;

  const value = useMemo(() => ({
    loading,
    requestsLoading,
    // Either window stopped before it answered (#647).
    failed: invoiceWindow.failed || requestWindow.failed,
    invoices,
    requests,
    pendingRequests: requests.filter(isPendingRequest),
    failedRequests: requests.filter(isFailedRequest),
    // A family a finished run passed over for owing nothing, as a row of its
    // own (#579) — without it a run that skipped everybody left no trace.
    skippedRows: skippedInvoiceRows(requests),
    // Invoices a run was asked to email and could not — nobody on the family
    // has an address (#622, functions/invoicing/email.js): their ids.
    notEmailed: new Set(requests.flatMap((r) => (Array.isArray(r.notEmailed) ? r.notEmailed : [])
      .map((x) => x?.invoiceId).filter((id) => typeof id === 'string'))),
    invoiceCount: invoices.length,
    forFamily: (familyId) => invoices.filter((i) => i.familyId === familyId),
    byId: (id) => invoices.find((i) => i.id === id) || null,

    // Queue a generation request. Returns the request ids so the caller can
    // watch them; the invoices themselves arrive on the invoices listener when
    // the function finishes.
    //
    // ── Why this can be more than one request ───────────────────────────────
    //
    // A request may name at most a hundred families
    // (MAX_FAMILIES_PER_INVOICE_REQUEST — the reasoning is in domain/invoice.js,
    // and firestore.rules and the function both enforce it). A 200-family studio
    // selecting everybody gets two requests rather than a refusal:
    // `invoiceRequestDocuments` (src/domain/invoice.js) dedupes and splits, and
    // both land in one batch, so the whole run either queues or does not. Each
    // shows its own "Creating" row and finishes on its own. The ids are random,
    // one per document.
    //
    // MMS pages its family picker and invoices a page at a time. Ours is one
    // long list, so the paging happens here instead of in the picker.
    //
    // **A run the rules would refuse is refused here**, with the sentence the
    // drawer shows: a period that ends before it starts, a footer past the
    // bound. Queued, it would close the drawer as though it had worked and be
    // refused by `validInvoiceRequest` at the server, leaving a "Creating" row
    // that never resolves.
    requestGeneration: async (form) => {
      if (!user) return null;
      const docs = invoiceRequestDocuments(form, serverTimestamp());
      if (!docs.length) return [];
      const refusal = invoiceRequestRefusal(form);
      if (refusal) throw new InvoiceRequestRefused(refusal);
      const ids = [];
      const batch = writeBatch(db);
      for (const data of docs) {
        const id = freeId('ireq');
        ids.push(id);
        batch.set(docRef(user.uid, COLLECTIONS.invoiceRequests, id), data);
      }
      await batch.commit();
      // How many families, and nothing about them (#458).
      trackEvent(ANALYTICS.invoiceCreated, { families: new Set(form.familyIds || []).size });
      return ids;
    },
  }), [invoices, requests, loading, requestsLoading, invoiceWindow.failed, requestWindow.failed, user]);

  return <InvoicesContext.Provider value={value}>{children}</InvoicesContext.Provider>;
}

export function useInvoices() {
  const ctx = useContext(InvoicesContext);
  if (!ctx) throw new Error('useInvoices must be used within an InvoicesProvider');
  return ctx;
}
